import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { SILENT_ERRORS } from '../interceptors/error.interceptor';
import {
  Student,
  CreateStudentDto,
  UpdateStudentDto,
  StudentAssignment,
  StudentCodeAvailability,
} from '../interfaces/student';
import {
  StudentSubjectProgression,
  SubjectFavorite,
} from '../interfaces/student-subject-progression';
import {
  ApiCollectionResponse,
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  private baseUrl = `${environment.BASE_URL}/students`;

  constructor(private http: HttpClient) {}

  /**
   * Get all students with optional filters
   * @param learningPathId - Optional learning path filter
   * @param isActive - Optional status filter
   * @returns Observable with collection of Students
   */
  getStudents(
    learningPathId?: number,
    isActive?: boolean,
  ): Observable<ApiCollectionResponse<Student>> {
    let params = new HttpParams();

    if (learningPathId !== undefined && learningPathId !== null) {
      params = params.set('learningPathId', learningPathId.toString());
    }

    if (isActive !== undefined && isActive !== null) {
      params = params.set('isActive', isActive.toString());
    }

    return this.http.get<ApiCollectionResponse<Student>>(this.baseUrl, {
      params,
    });
  }

  /**
   * Get a student by ID
   * @param studentId - Student ID
   * @returns Observable with a single Student
   */
  getById(studentId: number): Observable<ApiSingleResponse<Student>> {
    return this.http.get<ApiSingleResponse<Student>>(
      `${this.baseUrl}/${studentId}`,
    );
  }

  /**
   * Get student by ID (database long ID)
   * Matches backend endpoint: GET /api/students/{id:long}
   */
  getStudentById(studentId: number): Observable<ApiSingleResponse<Student>> {
    return this.http.get<ApiSingleResponse<Student>>(
      `${this.baseUrl}/${studentId}`,
    );
  }

  /**
   * Get student by student code
   * Matches backend endpoint: GET /api/students/code/{studentCode}
   */
  getStudentByCode(
    studentCode: string,
  ): Observable<ApiSingleResponse<Student>> {
    return this.http.get<ApiSingleResponse<Student>>(
      `${this.baseUrl}/code/${studentCode}`,
    );
  }

  /**
   * Get students by learning path
   */
  getStudentsByLearningPath(
    learningPathId: number,
  ): Observable<ApiCollectionResponse<Student>> {
    return this.http.get<ApiCollectionResponse<Student>>(
      `${this.baseUrl}/learningpath/${learningPathId}`,
    );
  }

  /**
   * Get students by region
   */
  getStudentsByRegion(
    regionId: number,
  ): Observable<ApiCollectionResponse<Student>> {
    return this.http.get<ApiCollectionResponse<Student>>(
      `${this.baseUrl}/region/${regionId}`,
    );
  }

  /**
   * Get student by user ID (Keycloak GUID)
   * Matches backend endpoint: GET /api/students/by-user/{id:Guid}
   */
  getStudentByUserId(userId: string): Observable<ApiSingleResponse<Student>> {
    return this.http.get<ApiSingleResponse<Student>>(
      `${this.baseUrl}/by-user/${userId}`,
    );
  }

  /**
   * Create a new student
   * @param student - CreateStudentDto
   * @returns Observable with created Student
   */
  createStudent(
    student: CreateStudentDto,
  ): Observable<ApiSingleResponse<Student>> {
    return this.http.post<ApiSingleResponse<Student>>(this.baseUrl, student);
  }

  /**
   * Pose un nouveau mot de passe <b>temporaire</b> sur le compte de l'élève :
   * il devra en choisir un autre à sa connexion suivante.
   */
  resetPassword(studentId: number, password: string): Observable<ApiSimpleResponse> {
    return this.http.put<ApiSimpleResponse>(`${this.baseUrl}/${studentId}/password`, { password });
  }

  /**
   * Update an existing student
   * @param studentId - Student ID
   * @param student - UpdateStudentDto
   * @returns Observable with updated Student
   */
  updateStudent(
    studentId: number,
    student: UpdateStudentDto,
  ): Observable<ApiSingleResponse<Student>> {
    return this.http.put<ApiSingleResponse<Student>>(
      `${this.baseUrl}/${studentId}`,
      student,
    );
  }

  /**
   * Delete a student
   * @param studentId - Student ID
   * @returns Observable with deletion response
   */
  deleteStudent(studentId: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.baseUrl}/${studentId}`);
  }

  /**
   * Activate/Deactivate a student
   * @param studentId - Student ID
   * @param isActive - Active status
   * @returns Observable with updated Student
   */
  updateStatus(
    studentId: number,
    isActive: boolean,
  ): Observable<ApiSingleResponse<Student>> {
    return this.http.patch<ApiSingleResponse<Student>>(
      `${this.baseUrl}/${studentId}/status`,
      { isActive },
    );
  }

  /**
   * Check if student code is available
   * @param studentCode - Student code to check
   * @param excludeStudentId - Optional student ID to exclude (for edit)
   * @returns Observable with boolean
   */
  checkStudentCodeAvailability(
    studentCode: string,
    excludeStudentId?: number,
  ): Observable<{ isAvailable: boolean }> {
    let params = new HttpParams().set('studentCode', studentCode);

    if (excludeStudentId) {
      params = params.set('excludeId', excludeStudentId.toString());
    }

    // Le serveur répond dans l'enveloppe ApiResponse, comme partout ailleurs :
    // on la déballe ici plutôt que d'exposer deux formes de réponse aux
    // appelants. `isAvailable` faux par défaut — une réponse illisible ne doit
    // pas laisser croire que le code est libre.
    return this.http
      .get<ApiSingleResponse<StudentCodeAvailability>>(
        `${this.baseUrl}/check-code`,
        { params },
      )
      .pipe(
        map((response) => ({
          isAvailable: response?.data?.isAvailable ?? false,
        })),
      );
  }

  /**
   * Get assignments by student
   */
  getAssignmentsByStudent(
    studentId: number,
  ): Observable<ApiCollectionResponse<StudentAssignment>> {
    return this.http.get<ApiCollectionResponse<StudentAssignment>>(
      `${this.baseUrl}/${studentId}/assignments`,
    );
  }

  /**
   * Get the subjects + progression for the currently authenticated student.
   * Backend computes the progression rate and average score per subject.
   * Matches: GET /api/students/me/subjects/progression
   */
  getMySubjectsProgression(): Observable<
    ApiCollectionResponse<StudentSubjectProgression>
  > {
    return this.http.get<ApiCollectionResponse<StudentSubjectProgression>>(
      `${this.baseUrl}/me/subjects/progression`,
    );
  }

  /**
   * Same as above but for a specific student (admin / teacher view).
   * Matches: GET /api/students/{id}/subjects/progression
   *
   * <p>Silencieuse : c'est le contenu d'un onglet, dans un écran qui en a
   * d'autres. Le serveur exige désormais `students.read.all` ; si le compte
   * ne la porte pas, l'onglet affiche son état vide au lieu que
   * l'intercepteur n'éjecte tout l'écran vers `/unauthorized`.</p>
   */
  getSubjectsProgression(
    studentId: number,
  ): Observable<ApiCollectionResponse<StudentSubjectProgression>> {
    return this.http.get<ApiCollectionResponse<StudentSubjectProgression>>(
      `${this.baseUrl}/${studentId}/subjects/progression`,
      { context: new HttpContext().set(SILENT_ERRORS, true) },
    );
  }

  /**
   * Épingle ou retire une matière des favorites de l'élève connecté.
   * Matches: PUT|DELETE /api/students/me/subjects/{subjectId}/favorite
   */
  setSubjectFavorite(
    subjectId: number,
    isFavorite: boolean,
  ): Observable<ApiSingleResponse<SubjectFavorite>> {
    const url = `${this.baseUrl}/me/subjects/${subjectId}/favorite`;
    return isFavorite
      ? this.http.put<ApiSingleResponse<SubjectFavorite>>(url, {})
      : this.http.delete<ApiSingleResponse<SubjectFavorite>>(url);
  }
}
